import { api } from "@/api/api";
import {
  createResponseContextFromResponse,
  createUnstructuredErrorContext,
  LoggableError,
} from "@/log/log.ts";
import type Right from "@/models/api/Right";
import type User from "@/models/api/User";
import type newUser from "@/models/newUser";
import type UserRight from "@/models/UserRight";

export function fetchUserRights(userId: number): Promise<UserRight> {
  return api.fetchParsed(`/webapi/users/rights/${userId}`);
}

export function updateUserRights(
  userId: number,
  newUserRights: Right[],
): Promise<void> {
  return api.fetchParsed(`/webapi/users/rights/${userId}`, {
    requestOptions: {
      method: "POST",
      body: JSON.stringify(newUserRights),
    },
  });
}

export interface PasswordChangeResponse {
  valid: boolean;
  messages: string[];
}

export function changePassword(
  userId: number,
  userPassword: string,
  userPasswordVerify: string,
): Promise<PasswordChangeResponse> {
  return api.fetchParsed(`/webapi/users/password/${userId}`, {
    requestOptions: {
      method: "POST",
      body: JSON.stringify({
        password: userPassword,
        password_verify: userPasswordVerify,
      }),
    },
  });
}

export type createUserResponse =
  | {
      success: true;
      userId: number;
    }
  | {
      success: false;
      messages: string[];
    };

export async function createUser(
  user: newUser | User,
): Promise<createUserResponse> {
  const response = await api.fetch(`/webapi/users/data`, {
    requestOptions: {
      method: "POST",
      body: JSON.stringify(user),
    },
  });

  try {
    if (response.status === 200) {
      const content = await response.json();
      if (Number.isNaN(content?.id)) {
        throw new Error("unexpected response content");
      }
      return {
        success: true,
        userId: content.id,
      };
    } else if (response.status === 400) {
      const content = await response.json();
      return {
        success: false,
        messages: content,
      };
    } else {
      throw new Error("unexpected response status");
    }
  } catch (e) {
    throw await makeLoggableErrorForCreateUser(user, response, e);
  }
}

async function makeLoggableErrorForCreateUser(
  user: Partial<User>,
  response: Response,
  error?: unknown,
): Promise<LoggableError> {
  if (user.password) {
    delete user.password;
  }
  if (user.password_verify) {
    delete user.password_verify;
  }
  const responseContext = await createResponseContextFromResponse(response);
  const requestContext = createUnstructuredErrorContext({
    url: api.baseURL.value + `/webapi/users/data`,
    user,
  });
  return new LoggableError([requestContext, responseContext], {
    previousError: error,
  });
}

export function fetchUser(userId: number): Promise<User> {
  return api.fetchParsed(`/webapi/users/data/${userId}`);
}

export async function updateUser(user: Partial<User> & { id: number }) {
  const response = await api.fetch(`/webapi/users/data/${user.id}`, {
    requestOptions: {
      method: "POST",
      body: JSON.stringify(user),
    },
  });

  try {
    if (response.status === 200) {
      return {
        success: true,
      };
    } else if (response.status === 400) {
      const content = await response.json();
      return {
        success: false,
        messages: content,
      };
    } else {
      throw new Error("unexpected response status");
    }
  } catch (e) {
    throw await makeLoggableErrorForCreateUser(user, response, e);
  }
}

export function deleteUser(userId: number) {
  return api.fetchParsed(`/webapi/users/${userId}`, {
    requestOptions: {
      method: "DELETE",
    },
  });
}

export interface checkResponse {
  valid: boolean;
  messages: string[];
}

export function checkName(
  name: string,
  options: {
    userId?: number;
    abortSignal?: AbortSignal;
  } = {},
): Promise<checkResponse> {
  return api.fetchParsed(`/webapi/users/check/name`, {
    requestOptions: {
      method: "POST",
      body: JSON.stringify({
        name: name,
        userId: options.userId,
      }),
      signal: options.abortSignal,
    },
  });
}

export async function checkEmail(
  email: string,
  options: {
    userId?: number;
    abortSignal?: AbortSignal;
  } = {},
): Promise<checkResponse> {
  return await api.fetchParsed(`/webapi/users/check/email`, {
    requestOptions: {
      method: "POST",
      body: JSON.stringify({
        email: email,
        userId: options.userId,
      }),
      signal: options.abortSignal,
    },
  });
}

export async function checkPassword(password: string): Promise<checkResponse> {
  return await api.fetchParsed("/webapi/users/check/password", {
    requestOptions: {
      method: "POST",
      body: JSON.stringify({ password }),
    },
  });
}

/**
 * TODO:
 * DELETE /webapi/users/data/{userId}
 * POST /webapi/users/data
 * GET /webapi/users/rights/{userId}
 * POST /webapi/users/rights/{userId}
 * POST /webapi/users/check/password
 */
